import { Asset, AssetColumns } from "../../types";
import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import { AssetHeader } from "./AssetHeader";
import { TokenTable } from "./TokensTable";
import { FactsTable } from "./FactsTable";
import {
    ASSET_FEES_FACTS_COLUMNS,
    ASSET_LEGAL_AND_REGULATORY_FACTS_COLUMNS,
    ASSET_MARKET_DATA_FACTS_COLUMNS,
    ASSET_PRIMARY_MARKET_FACTS_COLUMNS,
    ASSET_SERVICE_PROVIDER_FACTS_COLUMNS,
    REAL_ESTATE_FACTS_COLUMNS,
    getAssetKeyFactsColumns,
} from "./consts";
import {
    isCommodity,
    isFiatCurrency,
    isRealEstate,
    isStablecoin,
    isUnderlyingStock,
} from "~/utils/isAssetClass";

import {
    AssetTimeseriesColumns as AssetTimeseriesColumnsV4,
    AssetTimeseriesGroupBy as AssetTimeseriesGroupByV4,
} from "~/modules/asset_timeseries_v4/types";
import {
    FilterMetadata,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import { TableCard } from "~/components/TableCard";
import { AssetTokenHolders } from "../../../token_holders/components/AssetTokenHolders";
import { AssetTransactions } from "./AssetTransactions";
import { AssetRatingsTable } from "~/modules/assets/components/AssetRatingsTable";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import { VisualizationCard } from "~/modules/visualization/components/VisualizationCard";
import { WindowResizeObserver } from "~/components/WindowResizeObserver";
import { AssetPieChart } from "~/modules/visualization/components/AssetPieChart";
import { AssetTimeSeriesCard } from "../../components/AssetTimeSeriesCard";
import { AssetList } from "../../components/AssetList";
import { DEFAULT_ASSET_LIST_QUERY } from "../../consts";
import { ASSET_CLASS_IDS } from "~/consts";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { AssetPieChartGroupBy } from "~/modules/visualization/components/AssetPieChart/types";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";
import { FeatureNames } from "~/modules/feature_flag/flags";
import { YieldTimeseriesChart } from "~/modules/visualization/components/YieldTimeseriesChart";
import {
    Props as AggregateDisplayProps,
    AggregateGrid,
} from "~/components/AggregateDisplay";
import { getValueByAssetClassID } from "~/utils/getValueByAssetClass";
import { PUBLIC_EQUITY_TABLE_COLUMNS } from "../../components/AssetTable/columns/PublicEquity";
import { DEFAULT_PIE_CHART_HEIGHT } from "~/modules/visualization/components/PieChart/consts";
import { hasAssetApyMeasure } from "../../lib/getAssetApy";
import { getAssetURL } from "~/utils/getAssetURL";
import { DashboardUnlockBoundary } from "~/modules/dashboard_page_unlocks/components/DashboardUnlockBoundary";
import { DashboardPageUnlockGate } from "~/modules/dashboard_page_unlocks/components/DashboardPageUnlockGate";

// // // //

function getHasData(asset: Asset): boolean {
    if (isFiatCurrency({ asset })) {
        return false;
    }

    if (isStablecoin({ asset })) {
        return !!asset.bridged_token_market_cap_dollar;
    }

    if (isRealEstate({ asset })) {
        return !!asset.total_asset_value_dollar;
    }

    return !!asset.bridged_token_value_dollar;
}

interface Props {
    asset: Asset;
    aggregates: AggregateDisplayProps[];
    underlyingStockFilterMetadata: FilterMetadata[];
}

export function AssetIndividualPage(props: Props) {
    const { asset, aggregates, underlyingStockFilterMetadata } = props;

    // Determine if the asset has data to display
    const hasData = getHasData(asset);

    const showPieChart =
        hasData &&
        !isCommodity({ asset }) &&
        !isRealEstate({ asset }) &&
        asset.token_count > 1;

    // Define a flag to conditionally show Tokenized Stocks table
    const isUnderlying = isUnderlyingStock({ asset });
    const useCompactMobileMetricGrid =
        aggregates.length === 4 || aggregates.length === 5;

    // Get the measure for the pie chart based on asset class
    const pieChartMeasure = getValueByAssetClassID<
        | "bridged_token_value_dollar"
        | "bridged_token_market_cap_dollar"
        | "total_asset_value_dollar"
    >({
        assetClassID: asset.asset_class_id,
        values: {
            stablecoin: "bridged_token_market_cap_dollar",
            real_estate: "total_asset_value_dollar",
        },
        fallbackValue: "bridged_token_value_dollar",
    });

    return (
        <>
            <AssetHeader asset={asset} />

            <AggregateGrid
                aggregates={aggregates}
                useCompactMobileMetricGrid={useCompactMobileMetricGrid}
            />

            <VisualizationGrid className="mt-4">
                <AssetTimeSeriesCard
                    asset={asset}
                    filters={
                        isUnderlying
                            ? [
                                  {
                                      field: TokenTimeseriesColumns.peggedAssetID,
                                      operator: FilterOperators.Equals,
                                      value: asset.id,
                                  },
                              ]
                            : [
                                  {
                                      field: TokenTimeseriesColumns.assetID,
                                      operator: FilterOperators.Equals,
                                      value: asset.id,
                                  },
                              ]
                    }
                    assetTimeSeriesFilters={
                        isUnderlying
                            ? [
                                  {
                                      field: AssetTimeseriesColumnsV4.peggedAssetID,
                                      operator: FilterOperators.Equals,
                                      value: asset.id,
                                  },
                              ]
                            : [
                                  {
                                      field: AssetTimeseriesColumnsV4.assetID,
                                      operator: FilterOperators.Equals,
                                      value: asset.id,
                                  },
                              ]
                    }
                    defaultGroupBy={
                        isUnderlying
                            ? TokenTimeseriesGroupBy.asset
                            : TokenTimeseriesGroupBy.network
                    }
                    enableGroupByControls={false}
                    columnSpanConfig={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                        xxl: showPieChart ? 8 : 12,
                    }}
                    includeApyChart={hasAssetApyMeasure(asset)}
                />

                {showPieChart && (
                    <VisualizationCard
                        title="Market Cap by Network"
                        colSpans={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                            xxl: 4,
                        }}
                        wrapperClassNameOverride=""
                        watermark="bottom-right"
                        showTimestamp={true}
                        cardClassName="[&>div:first-child]:md:items-start"
                    >
                        <WindowResizeObserver
                            defaultPlaceholderHeight={DEFAULT_PIE_CHART_HEIGHT}
                        >
                            <AssetPieChart
                                assets={[asset]}
                                groupBy={AssetPieChartGroupBy.network}
                                measure={pieChartMeasure}
                            />
                        </WindowResizeObserver>
                    </VisualizationCard>
                )}

                <FeatureFlag feature={FeatureNames.enableExperimentalFeatures}>
                    <YieldTimeseriesChart
                        groupBy={AssetTimeseriesGroupByV4.assetClass}
                        analyticsChartName="treasuries-yield-timeseries"
                        filters={[
                            {
                                field: AssetTimeseriesColumnsV4.assetID,
                                operator: FilterOperators.Equals,
                                value: asset.id,
                            },
                        ]}
                        columnSpanConfig={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                            xxl: 12,
                        }}
                    />
                </FeatureFlag>
            </VisualizationGrid>

            {!isUnderlying && (
                <VisualizationGrid className="my-6">
                    <TableCard
                        title="Key Facts"
                        colSpans={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: isUnderlying ? 12 : 6,
                            xxl: isUnderlying ? 12 : 6,
                        }}
                    >
                        <FactsTable
                            row={{
                                ...asset,
                                expanded: true,
                                isAsset: true,
                            }}
                            columns={getAssetKeyFactsColumns(
                                asset.asset_class_id,
                            )}
                            analyticsTableName="key_facts"
                        />
                    </TableCard>

                    <TableCard
                        title="Market Data"
                        colSpans={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 6,
                            xxl: 6,
                        }}
                    >
                        <FactsTable
                            row={{
                                ...asset,
                                expanded: true,
                                isAsset: true,
                            }}
                            columns={ASSET_MARKET_DATA_FACTS_COLUMNS}
                            includeBottomBorder
                            analyticsTableName="market_data"
                        />
                    </TableCard>
                </VisualizationGrid>
            )}

            {asset.tokens.length > 0 && (
                <VisualizationGrid>
                    <TableCard
                        title="Tokens"
                        colSpans={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                            xxl: 12,
                        }}
                    >
                        <TokenTable asset={asset} />
                    </TableCard>
                </VisualizationGrid>
            )}

            {!isUnderlying && (
                <VisualizationGrid className="mt-6">
                    <div className="col-span-12 xl:col-span-6 xxl:col-span-6 flex flex-col gap-6 justify-start">
                        <TableCard
                            title="Primary Market"
                            loginConfig={{
                                required: true,
                                analyticsLabel:
                                    AssetUserEvents.CardLoginToUnlock,
                                analyticsEventProperties: {
                                    card: "primary_market",
                                    asset_ticker: asset.ticker as string,
                                },
                            }}
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            }}
                        >
                            <FactsTable
                                row={{
                                    ...asset,
                                    expanded: true,
                                    isAsset: true,
                                }}
                                loginRequiredOnParentCard
                                columns={ASSET_PRIMARY_MARKET_FACTS_COLUMNS}
                                analyticsTableName="primary_market"
                            />
                        </TableCard>

                        {asset.ratings && asset.ratings.length > 0 && (
                            <TableCard
                                title="Assessments & Ratings"
                                colSpans={{
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12,
                                    xxl: 12,
                                }}
                            >
                                <AssetRatingsTable asset={asset} />
                            </TableCard>
                        )}

                        <TableCard
                            title="Service Providers"
                            loginConfig={{
                                required: true,
                                analyticsLabel:
                                    AssetUserEvents.CardLoginToUnlock,
                                analyticsEventProperties: {
                                    card: "fees",
                                    asset_ticker: asset.ticker as string,
                                },
                            }}
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            }}
                        >
                            <FactsTable
                                row={{
                                    ...asset,
                                    expanded: true,
                                    isAsset: true,
                                }}
                                loginRequiredOnParentCard
                                columns={ASSET_SERVICE_PROVIDER_FACTS_COLUMNS}
                                analyticsTableName="service_providers"
                            />
                        </TableCard>
                    </div>

                    <div className="col-span-12 xl:col-span-6 xxl:col-span-6 flex flex-col gap-6 justify-start">
                        {isRealEstate({ asset }) && (
                            <TableCard
                                title="Real Estate Metrics"
                                colSpans={{
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 6,
                                    xxl: 6,
                                }}
                            >
                                <FactsTable
                                    row={{
                                        ...asset,
                                        expanded: true,
                                        isAsset: true,
                                    }}
                                    columns={REAL_ESTATE_FACTS_COLUMNS}
                                    analyticsTableName="real_estate_metrics"
                                />
                            </TableCard>
                        )}

                        <TableCard
                            title="Legal & Regulatory"
                            loginConfig={{
                                required: true,
                                analyticsLabel:
                                    AssetUserEvents.CardLoginToUnlock,
                                analyticsEventProperties: {
                                    card: "legal_and_regulatory",
                                    asset_ticker: asset.ticker as string,
                                },
                            }}
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            }}
                        >
                            <FactsTable
                                row={{
                                    ...asset,
                                    expanded: true,
                                    isAsset: true,
                                }}
                                loginRequiredOnParentCard
                                columns={
                                    ASSET_LEGAL_AND_REGULATORY_FACTS_COLUMNS
                                }
                                analyticsTableName="legal_and_regulatory"
                            />
                        </TableCard>

                        <TableCard
                            title="Fees"
                            loginConfig={{
                                required: true,
                                analyticsLabel:
                                    AssetUserEvents.CardLoginToUnlock,
                                analyticsEventProperties: {
                                    card: "fees",
                                    asset_ticker: asset.ticker as string,
                                },
                            }}
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                                xxl: 12,
                            }}
                        >
                            <FactsTable
                                row={{
                                    ...asset,
                                    expanded: true,
                                    isAsset: true,
                                }}
                                loginRequiredOnParentCard
                                columns={ASSET_FEES_FACTS_COLUMNS}
                                analyticsTableName="fees"
                            />
                        </TableCard>
                    </div>
                </VisualizationGrid>
            )}

            {isUnderlying && (
                <AssetList
                    initialState={{
                        results: [],
                        sort: DEFAULT_ASSET_LIST_QUERY.sort,
                        pagination: {
                            page: 1,
                            perPage: 10,
                            pageCount: 1,
                            resultCount: 10,
                        },
                    }}
                    assetsQuery={{
                        ...DEFAULT_ASSET_LIST_QUERY,
                        filter: {
                            operator: FilterOperators.And,
                            filters: [
                                {
                                    field: AssetColumns.assetClassID,
                                    operator: FilterOperators.Equals,
                                    value: ASSET_CLASS_IDS.Stocks,
                                },
                                {
                                    field: AssetColumns.peggedAssetID,
                                    operator: FilterOperators.Equals,
                                    value: asset.id,
                                },
                            ],
                        },
                        sort: {
                            field: AssetColumns.bridgedTokenValueDollar,
                            direction: SortDirections.desc,
                        },
                    }}
                    filterMetadata={underlyingStockFilterMetadata}
                    title="Tokenized Public Stocks"
                    analyticsTableName="underlying_stock_assets"
                    columns={PUBLIC_EQUITY_TABLE_COLUMNS.filter(
                        (c) => c.slug !== AssetColumns.peggedAssetName,
                    )}
                />
            )}

            <VisualizationGrid className="my-6">
                <AssetTokenHolders asset={asset} />

                <AssetTransactions asset={asset} />
            </VisualizationGrid>

            {/* TODO - remove DashboardPageUnlockGate when we're ready to set this live for everyone */}
            <DashboardPageUnlockGate>
                <DashboardUnlockBoundary
                    pathname={getAssetURL({
                        asset: { ticker: asset.ticker, slug: asset.slug },
                    })}
                />
            </DashboardPageUnlockGate>
        </>
    );
}
